import cn from 'classnames'
import {FC, useState} from 'react'

import {IncludedPriceBenefits} from '@/components/common/benefits/IncludedPriceBenefits'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {FormSummaryCountryDetail} from '@/components/common/form/summary/common/FormSummaryCountryDetail'
import {FormSummaryItem} from '@/components/common/form/summary/common/layout/FormSummaryItem'
import {FormSummarySection} from '@/components/common/form/summary/common/layout/FormSummarySection'
import {FormSummary} from '@/components/common/form/summary/FormSummary'
import {FormSummaryPriceWrapper} from '@/components/common/form/summary/price/FormSummaryPriceWrapper'
import {usePreferences} from '@/components/context/Preferences'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {affiliateConfig} from '@/utils/affiliate/config'
import {DiscountCouponEntity} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {useVerifyDiscountCoupon} from '@/utils/data-fetchers/useVerifyDiscountCoupon'
import {calculateCountriesPrice, getMultiCountryDiscount} from '@/utils/pricing'
import {getActiveIpOfficePriceIncreaseInfo} from '@/utils/registration-request/utils'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {PricingClassSelect} from '../calculator/PricingClassSelect'

type PricingTrademarkRegistrationEstimationCardProps = {
  selectedJurisdictions: Jurisdiction[]
}

export const PricingTrademarkRegistrationEstimationCard: FC<
  PricingTrademarkRegistrationEstimationCardProps
> = ({selectedJurisdictions}) => {
  const {t} = useTranslation()
  const {currency, affiliate} = usePreferences()
  const [selectedClasses, setSelectedClasses] = useState<number>(1)
  const {
    title,
    description,
    vatNotIncludedNote,
    totalPriceTitle,
    countriesTitle,
    discountCouponTitle,
  } = getPricingContent().calculator

  const affiliateCoupon = affiliate ? affiliateConfig[affiliate].discountCoupon : null

  const result = useVerifyDiscountCoupon(affiliateCoupon, DiscountCouponEntity.registrationRequest)
  const price = calculateCountriesPrice(
    selectedJurisdictions,
    result?.data ?? null,
    false,
    currency,
    selectedClasses,
    0,
  )
  const estimatedPrice = price.total.vatBase
  const multiCountryDiscount = getMultiCountryDiscount(price.countries)
  const hasAppliedDiscount = price.couponDiscounts.length > 0
  const hasDiscount = multiCountryDiscount || hasAppliedDiscount

  const hasSelectedJurisdictions = selectedJurisdictions.length > 0

  const ipOfficePriceIncreaseInfo = getActiveIpOfficePriceIncreaseInfo().filter(({jurisdiction}) =>
    selectedJurisdictions.includes(jurisdiction),
  )

  return (
    <>
      <FormSummary
        title={t(title)}
        totalPrice={estimatedPrice}
        currency={currency}
        hasOverlay={false}
        ipOfficePriceIncreaseInfo={ipOfficePriceIncreaseInfo}
      >
        <div className="description">
          <IncludedPriceBenefits description={description} color={theme.palette.text.light} />
        </div>
        {hasSelectedJurisdictions && (
          <>
            <FormSummarySection label={t(countriesTitle)}>
              {price.countries.map(({country, legalFees, registrationFees}) => {
                const totalPrice = legalFees.total.vatBase + registrationFees.total.vatBase

                return (
                  <FormSummaryCountryDetail
                    key={country}
                    jurisdiction={country}
                    totalPrice={totalPrice}
                    serviceFee={legalFees.total.vatBase}
                    administrativeFee={registrationFees.total.vatBase}
                    trackedEvent={{
                      name: 'Price breakdown expanded',
                      parameters: {country, tab: 'Trademark registration', page: 'Pricing page'},
                    }}
                    currency={currency}
                  />
                )
              })}
            </FormSummarySection>
            <div className={cn('classes-select', hasDiscount && 'divider')}>
              <PricingClassSelect
                minSelectedClasses={1}
                selectedClasses={selectedClasses}
                setSelectedClasses={setSelectedClasses}
                title={{textVariant: TextVariant.caption, fontWeight: TextFontWeight.bold}}
                trackedEvent={{
                  name: 'Change number of classes (Pricing page)',
                  parameters: {tab: 'Trademark registration', page: 'Pricing page'},
                }}
              />
            </div>
            {hasAppliedDiscount && (
              <FormSummarySection label={t(discountCouponTitle)}>
                <FormSummaryItem
                  title={result?.data?.id ?? ''}
                  price={price.couponDiscountsTotal.vatBase}
                />
              </FormSummarySection>
            )}
          </>
        )}
        <FormSummaryPriceWrapper
          priceTitle={t(totalPriceTitle)}
          estimatedPrice={estimatedPrice}
          hasDiscountInput={false}
          multiCountryDiscount={multiCountryDiscount}
        />
        <div className="note">
          <Text variant={TextVariant.caption} element="p">
            {t(vatNotIncludedNote)}
          </Text>
        </div>
      </FormSummary>

      <style jsx>{`
        .note {
          margin-top: ${theme.spacing[2]};
          margin-bottom: ${theme.spacing[4]};
        }
        .classes-select {
          margin: ${theme.spacing[4]} 0;
        }
        .divider {
          padding-bottom: ${theme.spacing[4]};
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .description {
          margin-bottom: ${theme.spacing[4]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .note {
            margin-bottom: 0;
          }
          .description {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
